import { useMemo } from 'react'
import { createFilter } from 'react-select'

import {
  ClearIndicator,
  DropdownIndicator,
  filterConfig,
  reactSelectStyles,
  reactSelectTheme,
} from '@/utils/select.config'

export type UseSelectEditorParams = {
  eGridCell: HTMLElement
  colDef: { field?: string }
}

export type UseSelectEditorReturn = {
  field: string
  minWidth: number
  baseSelectProps: {
    id: string
    filterOption: ReturnType<typeof createFilter<unknown>>
    theme: typeof reactSelectTheme
    styles: Record<string, unknown>
    components: {
      ClearIndicator: typeof ClearIndicator
      DropdownIndicator: typeof DropdownIndicator
    }
    autoFocus: boolean
    isClearable: boolean
    menuPlacement: 'auto'
    menuPosition: 'fixed'
    menuShouldScrollIntoView: false
  }
}

/**
 * Shared react-select plumbing for grid cell editors.
 * Extracts minWidth, field, memoized styles and base props for Select/CreatableSelect.
 */
export function useSelectEditor({
  eGridCell,
  colDef,
}: UseSelectEditorParams): UseSelectEditorReturn {
  const minWidth = eGridCell.offsetWidth
  const field = colDef.field ?? 'name'

  const styles = useMemo(
    () => ({
      ...reactSelectStyles,
      control: (base: Record<string, unknown>) => ({ ...base, minWidth }),
    }),
    [minWidth],
  )

  const baseSelectProps = useMemo(
    () => ({
      id: field,
      filterOption: createFilter(filterConfig),
      theme: reactSelectTheme,
      styles,
      components: {
        ClearIndicator,
        DropdownIndicator,
      },
      autoFocus: true,
      isClearable: true,
      menuPlacement: 'auto' as const,
      menuPosition: 'fixed' as const,
      menuShouldScrollIntoView: false as const,
    }),
    [field, styles],
  )

  return {
    field,
    minWidth,
    baseSelectProps,
  }
}
